/**
 * Missed-Call ROI Calculator — client interactive.
 *
 * Classic link-magnet tool: prospect enters their monthly call volume +
 * conversion rate + deal size, we show dollars lost to missed calls and
 * dollars recoverable with BizRnR. Tool pages link well (Reddit, tool
 * roundups, industry blogs).
 *
 * Renders as a static form with defaults set so the page shows a sensible
 * computed number even before JS hydrates (good for Googlebot).
 */
'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { pushEvent } from '@/lib/analytics/core';

const INDUSTRY_DEFAULTS: { label: string; calls: number; conv: number; deal: number }[] = [
  { label: 'Custom',         calls: 500, conv: 40, deal: 250 },
  { label: 'HVAC / Plumbing', calls: 400, conv: 35, deal: 425 },
  { label: 'Dental practice', calls: 300, conv: 55, deal: 600 },
  { label: 'Real estate',     calls: 500, conv: 20, deal: 8000 },
  { label: 'Law firm',        calls: 250, conv: 30, deal: 4500 },
  { label: 'Auto dealership', calls: 800, conv: 15, deal: 1800 },
  { label: 'Roofing',         calls: 350, conv: 30, deal: 7500 },
  { label: 'Salon / spa',     calls: 450, conv: 50, deal: 85 },
];

const MISSED_RATE = 0.30; // Industry baseline: 25–40% of calls are missed on average.
const RECOVERY_WITH_BIZRNR = 0.95; // AI voice answers every call; realistic recovery ≈ 95%.

function fmtUsd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function Slider({ label, value, min, max, step, onChange, fmt }: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  fmt?: (v: number) => string;
}) {
  const id = `roi-slider-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label htmlFor={id} className="text-sm text-dark-muted">{label}</label>
        <span className="text-dark-text font-semibold tabular-nums">{fmt ? fmt(value) : value}</span>
      </div>
      <input
        id={id}
        data-testid={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary-500 cursor-pointer"
      />
    </div>
  );
}

export default function MissedCallRoiCalculator() {
  const [industry, setIndustry] = useState('Custom');
  const [calls, setCalls] = useState(500);
  const [convPct, setConvPct] = useState(40);
  const [dealValue, setDealValue] = useState(250);

  const { missedCalls, lostDeals, lostRevenue, recoveredRevenue, roi } = useMemo(() => {
    const missed = calls * MISSED_RATE;
    const deals = missed * (convPct / 100);
    const lost = deals * dealValue;
    const recovered = lost * RECOVERY_WITH_BIZRNR;
    // BizRnR Spark is $99/mo. ROI as multiple.
    const roi = recovered / 99;
    return { missedCalls: missed, lostDeals: deals, lostRevenue: lost, recoveredRevenue: recovered, roi };
  }, [calls, convPct, dealValue]);

  const applyPreset = (name: string) => {
    const p = INDUSTRY_DEFAULTS.find((x) => x.label === name);
    if (!p) return;
    setIndustry(name);
    setCalls(p.calls);
    setConvPct(p.conv);
    setDealValue(p.deal);
  };

  // Fire roi_calculation_submitted after the user stops adjusting inputs.
  // Debounced so slider drags don't flood the analytics pipe.
  const submitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInteractedRef = useRef(false);
  useEffect(() => {
    if (!hasInteractedRef.current) {
      hasInteractedRef.current = true;
      return; // skip initial mount
    }
    if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
    submitTimerRef.current = setTimeout(() => {
      pushEvent('roi_calculation_submitted', {
        industry,
        missed_calls: Math.round(missedCalls),
        avg_deal: dealValue,
      });
    }, 700);
    return () => {
      if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
    };
  }, [industry, calls, convPct, dealValue, missedCalls]);

  const trackCta = () => {
    pushEvent('calculator_cta_click', {
      tool: 'missed-call-roi',
      industry, calls, conv_pct: convPct, deal_value: dealValue,
      lost_revenue: Math.round(lostRevenue),
      recovered_revenue: Math.round(recoveredRevenue),
    });
  };

  return (
    <div
      data-testid="missed-call-roi-calculator"
      className="grid lg:grid-cols-5 gap-8 items-start"
    >
      {/* Inputs */}
      <div className="lg:col-span-3 bg-dark-card border border-dark-border rounded-2xl p-6 sm:p-8">
        <div className="mb-6">
          <label className="text-sm text-dark-muted block mb-2">Industry preset</label>
          <div className="flex flex-wrap gap-2">
            {INDUSTRY_DEFAULTS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p.label)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  industry === p.label
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'border-dark-border text-dark-muted hover:border-primary-500/40 hover:text-dark-text'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <Slider label="Inbound calls / month" value={calls} min={50} max={3000} step={50} onChange={setCalls} />
          <Slider label="Call-to-customer conversion rate" value={convPct} min={5} max={80} step={1} onChange={setConvPct} fmt={(v) => `${v}%`} />
          <Slider label="Avg deal / customer value" value={dealValue} min={50} max={20000} step={25} onChange={setDealValue} fmt={fmtUsd} />
        </div>
        <p className="text-xs text-dark-muted mt-6">
          Industry baseline: small businesses miss <strong>25–40%</strong> of inbound calls
          (this calculator uses 30%). BizRnR answers calls 24/7, so recovered revenue assumes a 95% capture rate.
        </p>
      </div>

      {/* Results */}
      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-2xl bg-dark-card border border-red-500/30 p-6">
          <p className="text-xs uppercase tracking-wider text-red-300 font-semibold mb-2">Revenue lost / month</p>
          <p
            data-testid="missed-call-roi-lost-revenue"
            className="text-4xl font-bold tabular-nums text-dark-text"
          >
            {fmtUsd(lostRevenue)}
          </p>
          <p className="text-sm text-dark-muted mt-2">
            {Math.round(missedCalls)} missed calls → {Math.round(lostDeals)} lost deals
          </p>
        </div>
        <div className="rounded-2xl bg-dark-card border border-primary-500/40 p-6">
          <p className="text-xs uppercase tracking-wider text-primary-400 font-semibold mb-2">Recoverable with BizRnR</p>
          <p
            data-testid="missed-call-roi-recovered-revenue"
            className="text-4xl font-bold tabular-nums text-primary-400"
          >
            {fmtUsd(recoveredRevenue)}
          </p>
          <p className="text-sm text-dark-muted mt-2">
            <strong className="text-dark-text">{roi >= 1 ? `${roi.toFixed(1)}× ROI` : 'Not yet positive'}</strong> vs. $99/mo AI Receptionist plan
          </p>
        </div>
        <Link
          href="/auth/register"
          onClick={trackCta}
          className="block text-center bg-primary-500 hover:bg-primary-600 text-white font-semibold py-4 rounded-xl transition-colors"
        >
          Get started →
        </Link>
        <Link
          href="/request-demo"
          onClick={trackCta}
          className="block text-center border border-dark-border hover:border-primary-500/50 text-dark-text font-semibold py-4 rounded-xl transition-colors"
        >
          See a live demo
        </Link>
      </div>
    </div>
  );
}
